import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  PlayArrow as OnboardIcon,
  SwapVert as TierIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Login as EnterTenantIcon,
  Tune as EntitlementsIcon,
  Info as DetailsIcon,
} from '@mui/icons-material';
import { TablePagination } from '@mui/material';
import {
  getTenants,
  getTenantDeletePreview,
  createTenant,
  updateTenant,
  deleteTenant,
  onboardTenant,
  getSubscriptionTiers,
  changeTenantTier,
  getTenantEntitlements,
  getTenantDetails,
  setTenantEntitlement,
  exportTenant,
  importTenant,
} from '../services/tenant.service';
import { openTenantAdminWorkspace } from '../utils/tenantAdminWorkspace';

const SUBSCRIPTION_TIERS = ['starter', 'growth', 'scale'];

const DetailField = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="textSecondary" display="block">{label}</Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Grid>
);

// notification_emails/phones are stored as [{email|phone, name}], but older
// rows hold bare strings. Render both rather than showing "[object Object]".
const contactList = (rows, key) => (rows || [])
  .map((row) => (typeof row === 'string' ? row : row?.[key]))
  .filter(Boolean)
  .join(', ');

function TenantManagementPage() {
  const [tenants, setTenants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // In-flight guard for the Create button — without this a slow response plus a
  // double-click makes two tenants.
  const [isCreating, setIsCreating] = useState(false);
  // The 409 payload when the backend refuses a duplicate, so we can show the
  // operator the tenant that already exists instead of quietly making another.
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tierDefinitions, setTierDefinitions] = useState([]);
  // Feature entitlement overrides (superadmin-only grants/denials independent
  // of tier). `entitlements` holds the last-fetched rows; `entitlementDrafts`
  // holds in-progress note/expiry edits keyed by feature_key, separate from
  // the fetched rows so typing doesn't get clobbered by a re-fetch.
  const [entitlementsDialogOpen, setEntitlementsDialogOpen] = useState(false);
  // Superadmin-side view of a workspace. The people list is the part that has
  // no other home: opening a tenant as its admin shows the superadmin's own
  // account under My Profile, which reads as though it were the customer's.
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [entitlements, setEntitlements] = useState([]);
  const [entitlementsLoading, setEntitlementsLoading] = useState(false);
  const [entitlementSaving, setEntitlementSaving] = useState(null);
  const [entitlementDrafts, setEntitlementDrafts] = useState({});
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteResult, setDeleteResult] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    domain: '',
    billing_email: '',
    subscription_tier: 'growth',
    onboard: false,
    // Tenant admin user fields. Internal names keep the old owner prefix for compatibility.
    createOwner: true,
    ownerUsername: '',
    ownerPassword: '',
    ownerEmail: '',
    ownerFullName: '',
  });

  // Load tenants on mount
  useEffect(() => {
    loadTenants();
    loadTierDefinitions();
  }, []);

  const loadTierDefinitions = async () => {
    try {
      const data = await getSubscriptionTiers();
      setTierDefinitions(data);
    } catch (err) {
      console.error('Failed to load tier definitions', err);
    }
  };

  const loadTenants = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTenants();
      setTenants(data);
    } catch (err) {
      setError('Failed to load tenants: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async ({ confirmDuplicate = false } = {}) => {
    // Guard the double-click. This is the actual cause of the duplicate
    // tenants: creation takes a few seconds (tenant + licence + onboarding), the
    // button stayed live the whole time, and a second click fired a second
    // request. The backend then silently auto-suffixed the slug and returned
    // 201, so the operator saw two successes and no warning. 'Serviti AI'
    // (serviti-ai) and 'Serviti AI' (serviti-ai-50988a) are one double-click.
    if (isCreating) return;
    setIsCreating(true);

    try {
      // Build tenant data with optional owner
      const tenantData = {
        name: formData.name,
        slug: formData.slug || undefined,
        domain: formData.domain || undefined,
        billing_email: formData.billing_email || undefined,
        subscription_tier: formData.subscription_tier,
        onboard: formData.onboard,
      };

      // Add tenant admin if creating one
      if (formData.createOwner && formData.ownerUsername && formData.ownerPassword) {
        tenantData.owner = {
          username: formData.ownerUsername,
          password: formData.ownerPassword,
          email: formData.ownerEmail || formData.billing_email || undefined,
          full_name: formData.ownerFullName || undefined,
        };
      }

      const newTenant = await createTenant(tenantData, { confirmDuplicate });
      setTenants([...tenants, newTenant]);
      const ownerMsg = formData.createOwner && formData.ownerUsername
        ? ` Tenant admin "${formData.ownerUsername}" created.`
        : '';
      setSuccess(`Tenant "${newTenant.name}" created successfully!${ownerMsg}`);
      setCreateDialogOpen(false);
      setDuplicateWarning(null);
      resetForm();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;

      // 409 = the backend refused a duplicate instead of quietly making one.
      // Show the operator the tenant that already exists, and let them choose.
      if (status === 409 && detail && typeof detail === 'object') {
        setDuplicateWarning(detail);
        setError(null);
      } else {
        setError(
          'Failed to create tenant: ' +
            (typeof detail === 'string' ? detail : detail?.message || err.message),
        );
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateTenant = async () => {
    if (!selectedTenant) return;
    try {
      const updated = await updateTenant(selectedTenant.id, formData);
      setTenants(tenants.map(t => t.id === updated.id ? updated : t));
      setSuccess(`Tenant "${updated.name}" updated successfully!`);
      setEditDialogOpen(false);
      setSelectedTenant(null);
      resetForm();
    } catch (err) {
      setError('Failed to update tenant: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteTenant = async () => {
    if (!selectedTenant || !deletePreview) return;
    try {
      setDeleteLoading(true);
      const result = await deleteTenant(selectedTenant.id, deleteConfirmation);
      setDeleteResult(result);
      setTenants(tenants.filter(t => t.id !== selectedTenant.id));
      setSuccess(`Tenant "${selectedTenant.name}" deleted successfully.`);
    } catch (err) {
      setError('Failed to delete tenant: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleOnboardTenant = async (tenant) => {
    try {
      const result = await onboardTenant(tenant.id);
      setSuccess(`Onboarding complete! Created ${result.prompts_created} prompts and ${result.agents_created} agents.`);
    } catch (err) {
      setError('Onboarding failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openTierDialog = (tenant) => {
    setSelectedTenant(tenant);
    setTierDialogOpen(true);
  };

  const handleChangeTier = async (newTier) => {
    if (!selectedTenant) return;
    try {
      const updated = await changeTenantTier(selectedTenant.id, newTier);
      setTenants(tenants.map(t => t.id === updated.id ? updated : t));
      const direction = SUBSCRIPTION_TIERS.indexOf(newTier) > SUBSCRIPTION_TIERS.indexOf(selectedTenant.subscription_tier) ? 'Upgraded' : 'Changed';
      setSuccess(`${direction} "${selectedTenant.name}" to ${newTier.charAt(0).toUpperCase() + newTier.slice(1)} tier!`);
      setTierDialogOpen(false);
      setSelectedTenant(null);
    } catch (err) {
      setError('Tier change failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openDetailsDialog = async (tenant) => {
    setSelectedTenant(tenant);
    setDetailsDialogOpen(true);
    setDetailsLoading(true);
    setDetailsError('');
    setDetails(null);
    try {
      // Contact details, allowances and people all come from this one call so
      // the dialog cannot show a stale limit next to a fresh usage number.
      setDetails(await getTenantDetails(tenant.id));
    } catch (err) {
      setDetailsError(err.response?.data?.detail || err.message || 'Could not load workspace details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const openEntitlementsDialog = async (tenant) => {
    setSelectedTenant(tenant);
    setEntitlementsDialogOpen(true);
    setEntitlementsLoading(true);
    try {
      const data = await getTenantEntitlements(tenant.id);
      setEntitlements(data);
      const drafts = {};
      data.forEach((row) => {
        drafts[row.feature_key] = {
          note: row.override?.note || '',
          expiresAt: row.override?.expires_at ? row.override.expires_at.slice(0, 10) : '',
        };
      });
      setEntitlementDrafts(drafts);
    } catch (err) {
      setError('Failed to load feature overrides: ' + (err.response?.data?.detail || err.message));
    } finally {
      setEntitlementsLoading(false);
    }
  };

  const handleEntitlementDraftChange = (featureKey, field, value) => {
    setEntitlementDrafts((prev) => ({
      ...prev,
      [featureKey]: { ...prev[featureKey], [field]: value },
    }));
  };

  // The date picker only collects a calendar date (YYYY-MM-DD). Sending that
  // as-is is read by the backend as 00:00, so the override would already be
  // expired for nearly the whole selected day. Send end-of-day UTC instead so
  // "Expires Aug 10" keeps the grant active through all of Aug 10.
  const endOfDayUtcIso = (dateStr) => (dateStr ? `${dateStr}T23:59:59` : null);

  // mode: 'inherit' | 'on' | 'off'. 'inherit' clears the override (value: null);
  // 'on'/'off' upserts it, carrying whatever note/expiry is in the draft.
  const handleSetEntitlement = async (featureKey, mode) => {
    if (!selectedTenant) return;
    setEntitlementSaving(featureKey);
    try {
      const draft = entitlementDrafts[featureKey] || {};
      const value = mode === 'inherit' ? null : mode === 'on';
      const updated = await setTenantEntitlement(selectedTenant.id, featureKey, {
        value,
        note: value === null ? null : draft.note,
        expiresAt: value === null ? null : endOfDayUtcIso(draft.expiresAt),
      });
      setEntitlements((prev) => prev.map((row) => (row.feature_key === featureKey ? updated : row)));
      setSuccess(`Updated "${updated.label}" for "${selectedTenant.name}".`);
    } catch (err) {
      setError('Failed to update feature override: ' + (err.response?.data?.detail || err.message));
    } finally {
      setEntitlementSaving(null);
    }
  };

  const handleExportTenant = async (tenant) => {
    try {
      await exportTenant(tenant.id, tenant.slug || tenant.name);
      setSuccess(`Exported "${tenant.name}" config — check your downloads.`);
    } catch (err) {
      setError('Export failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleImportTenant = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const text = await file.text();
        const jsonData = JSON.parse(text);
        const result = await importTenant(jsonData);
        setSuccess(`Imported tenant "${result.tenant?.name || result.name || 'new tenant'}" successfully!`);
        loadTenants();
      } catch (err) {
        if (err instanceof SyntaxError) {
          setError('Invalid JSON file.');
        } else {
          setError('Import failed: ' + (err.response?.data?.detail || err.message));
        }
      }
    };
    input.click();
  };

  const openEditDialog = async (tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name,
      slug: tenant.slug,
      domain: tenant.domain || '',
      billing_email: tenant.billing_email || '',
      subscription_tier: tenant.subscription_tier,
      is_active: tenant.is_active,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = async (tenant) => {
    setSelectedTenant(tenant);
    setDeletePreview(null);
    setDeleteResult(null);
    setDeleteConfirmation('');
    setDeleteDialogOpen(true);
    setDeletePreviewLoading(true);
    try {
      const preview = await getTenantDeletePreview(tenant.id);
      setDeletePreview(preview);
    } catch (err) {
      setError('Failed to load delete preview: ' + (err.response?.data?.detail || err.message));
      setDeleteDialogOpen(false);
      setSelectedTenant(null);
    } finally {
      setDeletePreviewLoading(false);
    }
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setSelectedTenant(null);
    setDeletePreview(null);
    setDeleteResult(null);
    setDeleteConfirmation('');
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      domain: '',
      billing_email: '',
      subscription_tier: 'trial',
      onboard: true,
      createOwner: true,
      ownerUsername: '',
      ownerPassword: '',
      ownerEmail: '',
      ownerFullName: '',
    });
  };

  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'scale': return 'primary';
      case 'growth': return 'secondary';
      case 'starter': return 'success';
      case 'trial': return 'warning';
      default: return 'default';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const q = searchTerm.trim().toLowerCase();
  const filteredTenants = q
    ? tenants.filter((t) =>
        [t.name, t.slug, t.domain, t.billing_email, t.subscription_tier]
          .some((f) => (f || '').toLowerCase().includes(q))
      )
    : tenants;
  const deleteAppRows = deletePreview?.app_db
    ? Object.entries(deletePreview.app_db).filter(([, item]) => (item.count || 0) > 0 || item.missing_table)
    : [];
  const requiredDeleteConfirmation = deletePreview?.confirmation_required || '';
  const canConfirmTenantDelete =
    Boolean(requiredDeleteConfirmation) &&
    deleteConfirmation.trim() === requiredDeleteConfirmation &&
    !deletePreview?.blocked &&
    !deleteResult;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" mb={3} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Tenant Management
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadTenants}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={handleImportTenant}
          >
            Import
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            New Tenant
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Tenants
              </Typography>
              <Typography variant="h4">
                {tenants.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Tenants
              </Typography>
              <Typography variant="h4">
                {tenants.filter(t => t.is_active).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Scale Tier
              </Typography>
              <Typography variant="h4">
                {tenants.filter(t => t.subscription_tier === 'scale').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Trial Tenants
              </Typography>
              <Typography variant="h4">
                {tenants.filter(t => t.trial_ends_at).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search tenants by name, slug, domain, email, or tier..."
        value={searchTerm}
        onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
        sx={{ mb: 2 }}
      />

      {/* Tenants Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Agents</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Domain</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Trial Ends</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTenants.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((tenant) => (
                <TableRow key={tenant.id} hover>
                  <TableCell
                    onClick={() => openTenantAdminWorkspace(tenant.id)}
                    sx={{ cursor: 'pointer', '&:hover .tenant-name': { color: 'primary.main', textDecoration: 'underline' } }}
                    title="Open as Tenant Admin"
                  >
                    <Typography fontWeight="medium" className="tenant-name">{tenant.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {tenant.billing_email || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(tenant.agents || []).length > 0 ? (
                      tenant.agents.map(name => (
                        <Chip key={name} label={name} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))
                    ) : (
                      <Typography variant="caption" color="textSecondary">No agents</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <code>{tenant.slug}</code>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{tenant.domain || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={tenant.subscription_tier}
                      color={getTierColor(tenant.subscription_tier)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tenant.is_active ? 'Active' : 'Inactive'}
                      color={tenant.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatDate(tenant.trial_ends_at)}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatDate(tenant.created_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Open as Tenant Admin">
                      <IconButton
                        size="small"
                        onClick={() => openTenantAdminWorkspace(tenant.id)}
                        color="primary"
                      >
                        <EnterTenantIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => openDetailsDialog(tenant)}
                        color="info"
                      >
                        <DetailsIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Config">
                      <IconButton
                        size="small"
                        onClick={() => handleExportTenant(tenant)}
                        color="info"
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Change Tier">
                      <IconButton
                        size="small"
                        onClick={() => openTierDialog(tenant)}
                        color="secondary"
                      >
                        <TierIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Feature Overrides">
                      <IconButton
                        size="small"
                        onClick={() => openEntitlementsDialog(tenant)}
                        color="secondary"
                      >
                        <EntitlementsIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Re-run Onboarding">
                      <IconButton
                        size="small"
                        onClick={() => handleOnboardTenant(tenant)}
                        color="primary"
                      >
                        <OnboardIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(tenant)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => openDeleteDialog(tenant)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filteredTenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="textSecondary" py={4}>
                      {searchTerm ? 'No tenants match your search.' : 'No tenants found. Click "New Tenant" to create one.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredTenants.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />

      {/* Create Tenant Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Tenant</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              name="name"
              label="Tenant Name"
              value={formData.name}
              onChange={handleFormChange}
              required
              fullWidth
            />
            <TextField
              name="slug"
              label="Slug (optional - auto-generated)"
              value={formData.slug}
              onChange={handleFormChange}
              fullWidth
              helperText="URL-friendly identifier"
            />
            <TextField
              name="domain"
              label="Domain"
              value={formData.domain}
              onChange={handleFormChange}
              fullWidth
              helperText="e.g., acme.serviti.ai"
            />
            <TextField
              name="billing_email"
              label="Billing Email"
              value={formData.billing_email}
              onChange={handleFormChange}
              fullWidth
              type="email"
            />
            <FormControl fullWidth>
              <InputLabel>Subscription Tier</InputLabel>
              <Select
                name="subscription_tier"
                value={formData.subscription_tier}
                onChange={handleFormChange}
                label="Subscription Tier"
              >
                {SUBSCRIPTION_TIERS.map(tier => (
                  <MenuItem key={tier} value={tier}>
                    {tier.charAt(0).toUpperCase() + tier.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  name="onboard"
                  checked={formData.onboard}
                  onChange={handleFormChange}
                />
              }
              label="Seed default prompts and agents"
            />
            
            {/* Tenant Admin User Section */}
            <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>
              Tenant Admin
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  name="createOwner"
                  checked={formData.createOwner}
                  onChange={handleFormChange}
                />
              }
              label="Create tenant admin"
            />
            {formData.createOwner && (
              <>
                <TextField
                  name="ownerUsername"
                  label="Tenant Admin Username"
                  value={formData.ownerUsername}
                  onChange={handleFormChange}
                  required
                  fullWidth
                />
                <TextField
                  name="ownerPassword"
                  label="Tenant Admin Password"
                  value={formData.ownerPassword}
                  onChange={handleFormChange}
                  type="password"
                  required
                  fullWidth
                />
                <TextField
                  name="ownerEmail"
                  label="Tenant Admin Email (optional)"
                  value={formData.ownerEmail}
                  onChange={handleFormChange}
                  fullWidth
                  type="email"
                />
                <TextField
                  name="ownerFullName"
                  label="Tenant Admin Full Name (optional)"
                  value={formData.ownerFullName}
                  onChange={handleFormChange}
                  fullWidth
                />
              </>
            )}
            {/* The backend refused a duplicate. Show the operator what already
                exists and make them choose, instead of quietly creating a second
                tenant with a suffixed slug. */}
            {duplicateWarning && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>This may already exist</AlertTitle>
                {duplicateWarning.message}
                {duplicateWarning.existing_tenant_slug && (
                  <Box sx={{ mt: 1, fontSize: '0.85rem' }}>
                    Existing: <strong>{duplicateWarning.existing_tenant_name}</strong>{' '}
                    (<code>{duplicateWarning.existing_tenant_slug}</code>)
                  </Box>
                )}
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setDuplicateWarning(null);
                      setCreateDialogOpen(false);
                      resetForm();
                    }}
                  >
                    Cancel — it already exists
                  </Button>
                  <Button
                    size="small"
                    color="warning"
                    variant="contained"
                    disabled={isCreating}
                    onClick={() => handleCreateTenant({ confirmDuplicate: true })}
                  >
                    {isCreating ? 'Creating…' : 'Yes, create separately'}
                  </Button>
                </Box>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={isCreating}>Cancel</Button>
          <Button
            onClick={() => handleCreateTenant()}
            variant="contained"
            disabled={
              isCreating ||
              !formData.name ||
              (formData.createOwner && (!formData.ownerUsername || !formData.ownerPassword))
            }
          >
            {isCreating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Tenant</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              name="name"
              label="Tenant Name"
              value={formData.name}
              onChange={handleFormChange}
              required
              fullWidth
            />
            <TextField
              name="domain"
              label="Domain"
              value={formData.domain}
              onChange={handleFormChange}
              fullWidth
            />
            <TextField
              name="billing_email"
              label="Billing Email"
              value={formData.billing_email}
              onChange={handleFormChange}
              fullWidth
              type="email"
            />
            <FormControl fullWidth>
              <InputLabel>Subscription Tier</InputLabel>
              <Select
                name="subscription_tier"
                value={formData.subscription_tier}
                onChange={handleFormChange}
                label="Subscription Tier"
              >
                {SUBSCRIPTION_TIERS.map(tier => (
                  <MenuItem key={tier} value={tier}>
                    {tier.charAt(0).toUpperCase() + tier.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleFormChange}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateTenant} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tenant Delete Preview / Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>
          Delete Tenant - {selectedTenant?.name}
        </DialogTitle>
        <DialogContent>
          {deletePreviewLoading ? (
            <Box display="flex" alignItems="center" gap={2} sx={{ py: 3 }}>
              <CircularProgress size={24} />
              <Typography>Loading delete preview...</Typography>
            </Box>
          ) : deleteResult ? (
            <Box>
              <Alert severity={deleteResult.success ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {deleteResult.success ? 'Tenant deletion completed.' : 'Tenant deletion finished with issues.'}
              </Alert>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Deleted users: {deleteResult.deleted?.users ?? 0}
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Deleted vector collections: {deleteResult.deleted?.vector_collections?.length ?? 0}
              </Typography>
              {(deleteResult.errors || []).length > 0 && (
                <Alert severity="error">
                  {deleteResult.errors.map((item, idx) => (
                    <div key={`${item.name || item.scope}-${idx}`}>
                      {item.scope}: {item.name} - {item.error}
                    </div>
                  ))}
                </Alert>
              )}
            </Box>
          ) : deletePreview ? (
            <Box>
              <Alert severity={deletePreview.blocked ? 'warning' : 'error'} sx={{ mb: 2 }}>
                {deletePreview.blocked
                  ? deletePreview.block_reason
                  : 'This permanently deletes the tenant, tenant users, app DB records, vector collections, and embeddings. This cannot be undone.'}
              </Alert>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">App DB Rows</Typography>
                    <Typography variant="h6">{deletePreview.summary?.app_rows ?? 0}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">Collections</Typography>
                    <Typography variant="h6">{deletePreview.summary?.tenant_collections ?? 0}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">Vector Collections</Typography>
                    <Typography variant="h6">{deletePreview.summary?.vector_collections ?? 0}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">Embeddings</Typography>
                    <Typography variant="h6">{deletePreview.summary?.vector_embeddings ?? 0}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>App DB records to delete</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 260 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Area</TableCell>
                      <TableCell align="right">Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deleteAppRows.map(([key, item]) => (
                      <TableRow key={key}>
                        <TableCell>
                          {item.label}
                          {item.missing_table && (
                            <Chip label="table missing" size="small" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell align="right">{item.count ?? 0}</TableCell>
                      </TableRow>
                    ))}
                    {deleteAppRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2}>No tenant-scoped app records found.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Vector collections to delete</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 220 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Collection</TableCell>
                      <TableCell>Backend</TableCell>
                      <TableCell align="right">Embeddings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(deletePreview.vector_db || []).map((item) => (
                      <TableRow key={item.collection_name}>
                        <TableCell>
                          <code>{item.collection_name}</code>
                          {item.error && (
                            <Typography variant="caption" color="error" display="block">
                              {item.error}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{item.backend || '-'}</TableCell>
                        <TableCell align="right">
                          {item.embedding_count ?? 'unknown'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(deletePreview.vector_db || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>No vector collections found.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {!deletePreview.blocked && (
                <>
                  <Typography sx={{ mb: 1 }}>
                    To confirm, type <strong>{requiredDeleteConfirmation}</strong> below:
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={requiredDeleteConfirmation}
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && canConfirmTenantDelete && handleDeleteTenant()}
                  />
                </>
              )}
            </Box>
          ) : (
            <Alert severity="warning">Delete preview is not available.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>
            {deleteResult ? 'Close' : 'Cancel'}
          </Button>
          {!deleteResult && (
            <Button
              onClick={handleDeleteTenant}
              color="error"
              variant="contained"
              disabled={!canConfirmTenantDelete || deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete Tenant'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Tier Change Dialog */}
      <Dialog open={tierDialogOpen} onClose={() => setTierDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Change Subscription — {selectedTenant?.name}
        </DialogTitle>
        <DialogContent>
          {selectedTenant && (
            <Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Current tier: <strong>{selectedTenant.subscription_tier?.charAt(0).toUpperCase() + selectedTenant.subscription_tier?.slice(1)}</strong>
              </Typography>

              <Grid container spacing={2}>
                {(tierDefinitions.length > 0 ? tierDefinitions : SUBSCRIPTION_TIERS.map(t => ({ id: t, label: t.charAt(0).toUpperCase() + t.slice(1), description: '', max_users: '-', max_agents: '-', max_knowledge_pages: '-' }))).map((tier) => {
                  const isCurrent = selectedTenant.subscription_tier === tier.id;
                  const currentIdx = SUBSCRIPTION_TIERS.indexOf(selectedTenant.subscription_tier);
                  const tierIdx = SUBSCRIPTION_TIERS.indexOf(tier.id);
                  const isUpgrade = tierIdx > currentIdx;

                  return (
                    <Grid item xs={12} sm={6} md={3} key={tier.id}>
                      <Card
                        variant={isCurrent ? 'outlined' : 'elevation'}
                        sx={{
                          height: '100%',
                          border: isCurrent ? '2px solid' : '1px solid',
                          borderColor: isCurrent ? 'primary.main' : 'divider',
                          opacity: isCurrent ? 0.85 : 1,
                        }}
                      >
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" gutterBottom>
                            {tier.label}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, minHeight: 40 }}>
                            {tier.description}
                          </Typography>
                          <Box sx={{ mb: 2, textAlign: 'left' }}>
                            <Typography variant="body2">AI answers/month: <strong>{(tier.max_ai_answers_inr ?? tier.max_ai_answers)?.toLocaleString() || '-'} (INR) / {tier.max_ai_answers?.toLocaleString() || '-'} (USD)</strong></Typography>
                            <Typography variant="body2">Websites: <strong>{tier.max_websites ?? '-'}</strong></Typography>
                            <Typography variant="body2">Users: <strong>{tier.max_users}</strong></Typography>
                            <Typography variant="body2">WhatsApp: <strong>{tier.included_notification_phones ?? '-'}</strong></Typography>
                            <Typography variant="body2">Email: <strong>{tier.included_notification_emails ?? '-'}</strong></Typography>
                            <Typography variant="body2">Knowledge pages: <strong>{typeof tier.max_knowledge_pages === 'number' ? tier.max_knowledge_pages.toLocaleString() : (tier.max_knowledge_pages ?? '-')}</strong></Typography>
                          </Box>
                          {isCurrent ? (
                            <Chip label="Current Plan" color="primary" size="small" />
                          ) : (
                            <Button
                              variant={isUpgrade ? 'contained' : 'outlined'}
                              color={isUpgrade ? 'primary' : 'warning'}
                              size="small"
                              fullWidth
                              onClick={() => handleChangeTier(tier.id)}
                            >
                              {isUpgrade ? 'Upgrade' : 'Downgrade'}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTierDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Feature Overrides Dialog */}
      <Dialog
        open={entitlementsDialogOpen}
        onClose={() => setEntitlementsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Feature Overrides — {selectedTenant?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Grant or deny an individual feature for this tenant, independent of its{' '}
            <strong>{selectedTenant?.subscription_tier}</strong> plan. Tier defaults are unchanged for
            every other tenant. "Inherit" reverts to the plan default.
          </Typography>
          {entitlementsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            entitlements.map((row) => {
              const draft = entitlementDrafts[row.feature_key] || { note: '', expiresAt: '' };
              const mode = row.override ? (row.override.value ? 'on' : 'off') : 'inherit';
              const saving = entitlementSaving === row.feature_key;
              return (
                <Card key={row.feature_key} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Box>
                        <Typography variant="subtitle1">{row.label}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Plan default: <strong>{row.tier_default ? 'On' : 'Off'}</strong>
                          {' · '}Effective: <strong>{row.effective ? 'On' : 'Off'}</strong>
                        </Typography>
                      </Box>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={mode}
                        disabled={saving}
                        onChange={(_e, next) => next && handleSetEntitlement(row.feature_key, next)}
                      >
                        <ToggleButton value="inherit">Inherit</ToggleButton>
                        <ToggleButton value="on">On</ToggleButton>
                        <ToggleButton value="off">Off</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    {mode !== 'inherit' && (
                      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                        <TextField
                          label="Note"
                          size="small"
                          value={draft.note}
                          onChange={(e) => handleEntitlementDraftChange(row.feature_key, 'note', e.target.value)}
                          sx={{ flex: 1, minWidth: 200 }}
                          placeholder="Why this override exists (e.g. founding-brand deal)"
                        />
                        <TextField
                          label="Expires (end of day, UTC)"
                          type="date"
                          size="small"
                          value={draft.expiresAt}
                          onChange={(e) => handleEntitlementDraftChange(row.feature_key, 'expiresAt', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={saving}
                          onClick={() => handleSetEntitlement(row.feature_key, mode)}
                        >
                          Save note / expiry
                        </Button>
                      </Box>
                    )}
                    {row.override?.changed_by && (
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                        Last changed {new Date(row.override.changed_at).toLocaleString()}
                        {row.override.expires_at ? ` · expires ${new Date(row.override.expires_at).toLocaleDateString()}` : ''}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEntitlementsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedTenant?.name}
          <Typography variant="caption" color="textSecondary" display="block">
            <code>{selectedTenant?.slug}</code> &middot; {selectedTenant?.id}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {detailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={28} /></Box>
          ) : detailsError ? (
            <Alert severity="error">{detailsError}</Alert>
          ) : details && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Workspace</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <DetailField label="Domain" value={details.domain} />
                <DetailField label="Plan" value={details.subscription_tier} />
                <DetailField label="Subscription status" value={details.subscription_status} />
                <DetailField label="Active" value={details.is_active ? 'Yes' : 'No'} />
                <DetailField label="Trial ends" value={formatDate(details.trial_ends_at)} />
                <DetailField label="Created" value={formatDate(details.created_at)} />
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Contact</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <DetailField label="Billing email" value={details.billing_email} />
                <DetailField label="Business number" value={details.business_phone} />
                <DetailField label="Lead &amp; support emails" value={contactList(details.notification_emails, 'email')} />
                <DetailField label="WhatsApp numbers" value={contactList(details.notification_phones, 'phone')} />
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Allowances</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {(details.allowances || []).map((row) => (
                  <DetailField key={row.key} label={row.label} value={`${row.used} of ${row.limit} used`} />
                ))}
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Agents ({(details.agents || []).length})
              </Typography>
              <Box sx={{ mb: 3 }}>
                {(details.agents || []).length > 0 ? (
                  details.agents.map((agent) => (
                    <Chip
                      key={agent.name}
                      label={agent.is_active ? agent.name : `${agent.name} (disabled)`}
                      size="small"
                      variant="outlined"
                      color={agent.is_active ? 'default' : 'warning'}
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">No agents</Typography>
                )}
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>People</Typography>
              {(details.people || []).length === 0 ? (
                <Typography variant="body2" color="textSecondary">No users in this workspace</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Username</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Workspace role</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Joined</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {details.people.map((person) => (
                        <TableRow key={person.id}>
                          <TableCell>{person.full_name || '—'}</TableCell>
                          <TableCell><code>{person.username}</code></TableCell>
                          <TableCell>{person.email || '—'}</TableCell>
                          <TableCell>{person.tenant_role || person.role || '—'}</TableCell>
                          <TableCell>
                            <Chip
                              label={person.is_active ? 'Active' : 'Inactive'}
                              color={person.is_active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{person.joined_at ? formatDate(person.joined_at) : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => openTenantAdminWorkspace(selectedTenant.id)}>Open as Tenant Admin</Button>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default TenantManagementPage;
